import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { apiClient } from '@/services/api';
import type { TableZoneInfo } from '@/types/api';
import { X } from 'lucide-react';

interface ZoneModalProps {
  restaurantId: number;
  floorId: number;
  zone: TableZoneInfo | null;
  onClose: () => void;
  onSuccess: () => void;
}

export default function ZoneModal({ restaurantId, floorId, zone, onClose, onSuccess }: ZoneModalProps) {
  const [formData, setFormData] = useState({
    name: zone?.name || '',
    description: zone?.description || '',
    color: zone?.color || '',
    enabled: zone?.enabled ?? true,
    sortOrder: zone?.sortOrder ?? 0,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const createMutation = useMutation({
    mutationFn: (data: Partial<TableZoneInfo>) =>
      apiClient.createZone(restaurantId, floorId, data),
    onSuccess,
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to create zone';
      setErrors({ submit: message });
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: Partial<TableZoneInfo>) =>
      apiClient.updateZone(restaurantId, zone!.id, data),
    onSuccess,
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to update zone';
      setErrors({ submit: message });
    },
  });

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Zone name is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    const submitData: Partial<TableZoneInfo> = {
      name: formData.name,
      description: formData.description || undefined,
      color: formData.color || undefined,
      enabled: formData.enabled,
      sortOrder: formData.sortOrder,
    };

    if (zone) {
      await updateMutation.mutateAsync(submitData);
    } else {
      await createMutation.mutateAsync(submitData);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        className="card max-w-md w-full mx-4 p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-900">
            {zone ? 'Edit Zone' : 'Add New Zone'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md p-2 min-h-[44px] min-w-[44px] flex items-center justify-center"
            aria-label="Close modal"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {errors.submit && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
            {errors.submit}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="zone-name" className="block text-sm font-medium text-gray-700 mb-1">
              Zone Name *
            </label>
            <input
              id="zone-name"
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className={`input w-full ${errors.name ? 'border-red-500' : ''}`}
              placeholder="e.g., Patio, VIP Section"
            />
            {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
          </div>

          <div>
            <label htmlFor="zone-description" className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <input
              id="zone-description"
              type="text"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="input w-full"
              placeholder="Optional description"
            />
          </div>

          <div>
            <label htmlFor="zone-color" className="block text-sm font-medium text-gray-700 mb-1">
              Color
            </label>
            <div className="flex items-center space-x-3">
              <input
                id="zone-color-picker"
                type="color"
                value={formData.color || '#000000'}
                onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                className="h-10 w-14 rounded border border-gray-300 cursor-pointer p-0.5"
              />
              <input
                id="zone-color"
                type="text"
                value={formData.color}
                onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                className="input flex-1"
                placeholder="e.g., #FF5733"
              />
            </div>
          </div>

          <div>
            <label htmlFor="zone-sort-order" className="block text-sm font-medium text-gray-700 mb-1">
              Sort Order
            </label>
            <input
              id="zone-sort-order"
              type="number"
              value={formData.sortOrder}
              onChange={(e) => setFormData({ ...formData, sortOrder: parseInt(e.target.value) || 0 })}
              className="input w-full"
            />
          </div>

          <div className="flex items-center">
            <input
              id="zone-enabled"
              type="checkbox"
              checked={formData.enabled}
              onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
              className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <label htmlFor="zone-enabled" className="ml-2 text-sm text-gray-700">
              Enabled
            </label>
          </div>

          <div className="flex space-x-3 pt-4 border-t border-gray-200">
            <button type="button" onClick={onClose} className="btn btn-outline flex-1 h-10">
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary flex-1 h-10"
              disabled={createMutation.isPending || updateMutation.isPending}
            >
              {createMutation.isPending || updateMutation.isPending
                ? 'Saving...'
                : zone
                ? 'Update Zone'
                : 'Create Zone'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
